// ---------------------------------------------------------------------------
// Subscriber ("simple mode") nav model.
//
// A subscriber's left nav is built from the agents their business actually runs
// (TenantContext.activeAgentKinds) instead of the full operator menu. Each
// active agent contributes a "My Agents" entry (its /agents/:kind workspace)
// plus the operational surfaces that agent implies; a small base set (Home,
// Knowledge, Connections, Settings…) is always shown. Operator-only tooling
// (Agent Fleet page, Mission Control, Platform) is never emitted here.
//
// Output matches the Sidebar's NavGroup/NavItem shape so it plugs straight in.
// ---------------------------------------------------------------------------

import { AGENT_META, agentLabel } from '@/components/ui/Icons';

export interface SimpleNavItem { to: string; label: string; feature?: string }
export interface SimpleNavGroup { id: string; label: string; icon: string; items: SimpleNavItem[] }

// Group glyphs reused from Sidebar's NAV_GROUPS so the two modes look identical.
const GROUP_ICON = {
  dashboard: 'M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10',
  agents: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  operations: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  knowledge: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  setup: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
};

/**
 * Operational surfaces each agent kind unlocks. Kinds not listed contribute
 * only their My-Agents workspace entry (its /agents/:kind page IS the dashboard,
 * e.g. inventory, accounting, marketer, reviews).
 */
const AGENT_SURFACES: Record<string, SimpleNavItem[]> = {
  rafaels: [
    { to: '/conversations', label: 'Conversations' },
    { to: '/orders', label: 'Orders' },
    { to: '/catalog', label: 'Services & Prices' },
  ],
  scheduler: [{ to: '/orders/calendar', label: 'Calendar' }],
  growth: [{ to: '/growth', label: 'Growth Coach', feature: 'growth' }],
};

/** Kinds you can talk to somewhere in the app — any of these active means the
 * tenant gets a Test Chat entry (not just the rafaels/rafael channel bots). */
const CHAT_CAPABLE_KINDS = new Set([
  'scheduler', 'marketer', 'inventory', 'accounting', 'reviews', 'growth', 'rafaels', 'rafael',
]);

/** Build the tailored subscriber nav from the tenant's live agents. */
export function buildSimpleNav(
  activeAgentKinds: string[],
  hasFeature: (key: string) => boolean,
): SimpleNavGroup[] {
  const groups: SimpleNavGroup[] = [
    { id: 'dashboard', label: 'Dashboard', icon: GROUP_ICON.dashboard, items: [{ to: '/', label: 'Home' }] },
  ];

  // My Agents — one entry per active, known agent kind, plus Test Chat when
  // any active kind is chat-capable.
  const agentItems: SimpleNavItem[] = activeAgentKinds
    .filter((k) => AGENT_META[k])
    .map((k) => ({ to: `/agents/${k}`, label: agentLabel(k) }));
  if (activeAgentKinds.some((k) => CHAT_CAPABLE_KINDS.has(k))) {
    agentItems.push({ to: '/test-chat', label: 'Test Chat' });
  }
  if (agentItems.length) {
    groups.push({ id: 'agents', label: 'My Agents', icon: GROUP_ICON.agents, items: agentItems });
  }

  // Operations — union of surfaces unlocked by the active agents (deduped, feature-checked).
  const seen = new Set<string>();
  const opsItems: SimpleNavItem[] = [];
  for (const kind of activeAgentKinds) {
    for (const item of AGENT_SURFACES[kind] ?? []) {
      if (item.feature && !hasFeature(item.feature)) continue;
      if (seen.has(item.to)) continue;
      seen.add(item.to);
      opsItems.push(item);
    }
  }
  if (opsItems.length) {
    groups.push({ id: 'operations', label: 'Operations', icon: GROUP_ICON.operations, items: opsItems });
  }

  // Always-available subscriber essentials.
  groups.push({
    id: 'knowledge', label: 'Knowledge', icon: GROUP_ICON.knowledge, items: [
      { to: '/knowledge', label: 'Knowledge' },
    ],
  });
  groups.push({
    id: 'setup', label: 'Setup', icon: GROUP_ICON.setup, items: [
      { to: '/connections', label: 'Connections' },
      { to: '/team', label: 'Team' },
      { to: '/settings', label: 'Settings' },
    ],
  });

  return groups.filter((g) => g.items.length > 0);
}
